'use client';

import type {
  MysteryRoomDoorRuntimeState,
  MysteryRoomNpcState,
} from '../core/localRuntime';
import { canDoorActorUseDoor, type DoorActor } from './doorSystem';
import type {
  MysteryRoomAccessLevel,
  MysteryRoomCollisionShape,
  MysteryRoomCustomObjectDefinition,
  MysteryRoomMapDefinition,
  MysteryRoomNavEdgeDefinition,
  MysteryRoomNavNodeDefinition,
  MysteryRoomRect,
  MysteryRoomVec2,
} from '../world/mapTypes';
import { rectContainsPoint } from '../world/mapTypes';
import { isMysteryRoomCustomObjectAlphaOpaqueAtPoint } from '../world/assetAlphaCollision';

const NAV_WAYPOINT_ARRIVAL_MARGIN = 8;
const NAV_CANDIDATE_LIMIT = 4;
const GRID_ROUTE_CELL_SIZE = 48;
const GRID_ROUTE_VISIT_LIMIT = 6000;
const HAZARD_ROUTE_COST_MULTIPLIER = 36;
const SNAP_SEARCH_STEPS = 48;
const SNAP_SEARCH_MAX_RADIUS = 520;

type NavigationActor = {
  accessLevels: MysteryRoomAccessLevel[];
  actorType?: DoorActor['actorType'];
  inventoryItemIds?: string[];
  position: MysteryRoomVec2;
  zoneId: string;
};
type DoorNavigationActor = Pick<DoorActor, 'actorType' | 'accessLevels' | 'inventoryItemIds'>;

export type MysteryRoomNavGraphCache = {
  edgeByNodePair: Map<string, MysteryRoomNavEdgeDefinition>;
  nodeById: Map<string, MysteryRoomNavNodeDefinition>;
  nodeIds: string[];
  nodesByZoneId: Map<string, MysteryRoomNavNodeDefinition[]>;
  outgoingEdgesByNodeId: Map<string, MysteryRoomNavEdgeDefinition[]>;
};

const navGraphCacheByMap = new WeakMap<MysteryRoomMapDefinition, MysteryRoomNavGraphCache>();

export function getMysteryRoomNavGraphCache(map: MysteryRoomMapDefinition): MysteryRoomNavGraphCache {
  const cached = navGraphCacheByMap.get(map);
  if (cached) {
    return cached;
  }

  const nodeById = new Map<string, MysteryRoomNavNodeDefinition>();
  const nodesByZoneId = new Map<string, MysteryRoomNavNodeDefinition[]>();
  const outgoingEdgesByNodeId = new Map<string, MysteryRoomNavEdgeDefinition[]>();
  const edgeByNodePair = new Map<string, MysteryRoomNavEdgeDefinition>();

  for (const node of map.navNodes) {
    nodeById.set(node.id, node);
    const zoneNodes = nodesByZoneId.get(node.zoneId) ?? [];
    zoneNodes.push(node);
    nodesByZoneId.set(node.zoneId, zoneNodes);
  }

  for (const edge of map.navEdges) {
    const outgoing = outgoingEdgesByNodeId.get(edge.fromNodeId) ?? [];
    outgoing.push(edge);
    outgoingEdgesByNodeId.set(edge.fromNodeId, outgoing);

    const pairKey = navNodePairKey(edge.fromNodeId, edge.toNodeId);
    if (!edgeByNodePair.has(pairKey)) {
      edgeByNodePair.set(pairKey, edge);
    }
  }

  const nextCache = {
    edgeByNodePair,
    nodeById,
    nodeIds: map.navNodes.map((node) => node.id),
    nodesByZoneId,
    outgoingEdgesByNodeId,
  };
  navGraphCacheByMap.set(map, nextCache);
  return nextCache;
}

function navNodePairKey(fromNodeId: string, toNodeId: string) {
  return `${fromNodeId}::${toNodeId}`;
}

export type MysteryRoomPointNavigationRoute = {
  nodePath: string[];
  reachable: boolean;
  targetZoneId: string | null;
  waypoints: MysteryRoomVec2[];
};

export function buildNpcNavPath(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  npc: MysteryRoomNpcState,
  targetNodeId: string,
): string[] {
  const startNodeId = findNearestNavNodeId(map, npc.position, npc.zoneId);
  const targetNode = getMysteryRoomNavGraphCache(map).nodeById.get(targetNodeId);

  if (!startNodeId || !targetNode) {
    return [];
  }

  return shortestPath(
    map,
    doors,
    { accessLevels: npc.accessLevels, actorType: 'npc' },
    startNodeId,
    targetNodeId,
    npc.radius,
    { allowBestApproach: false },
  );
}

/** Клик в стену/пустоту: цель маршрута — ближайшая проходимая точка на полу. */
function resolveNavigationTargetPoint(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: NavigationActor,
  targetPoint: MysteryRoomVec2,
  radius: number,
): MysteryRoomVec2 {
  if (canOccupyPoint(map, targetPoint, doors, radius, actor)) {
    return { ...targetPoint };
  }
  const snapped = snapToNearestReachablePoint(map, doors, actor, targetPoint, radius);
  return snapped ? { ...snapped } : { ...targetPoint };
}

function clampPointToRect(point: MysteryRoomVec2, rect: MysteryRoomRect): MysteryRoomVec2 {
  return {
    x: Math.max(rect.x, Math.min(rect.x + rect.width, point.x)),
    y: Math.max(rect.y, Math.min(rect.y + rect.height, point.y)),
  };
}

function snapToNearestReachablePoint(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: NavigationActor,
  targetPoint: MysteryRoomVec2,
  radius: number,
): MysteryRoomVec2 | null {
  const floors = map.rooms.flatMap((room) => room.floor);
  if (floors.length === 0) {
    return null;
  }

  const clamped = floors
    .map((rect) => {
      const point = clampPointToRect(targetPoint, rect);
      return { rect, point, dist: distanceBetween(targetPoint, point) };
    })
    .sort((a, b) => a.dist - b.dist);

  for (const entry of clamped) {
    if (canOccupyPoint(map, entry.point, doors, radius, actor)) {
      return entry.point;
    }
  }

  const seed = clamped[0]?.point ?? targetPoint;
  for (let radiusPx = 12; radiusPx <= SNAP_SEARCH_MAX_RADIUS; radiusPx += 28) {
    for (let step = 0; step < SNAP_SEARCH_STEPS; step += 1) {
      const angle = (step / SNAP_SEARCH_STEPS) * Math.PI * 2;
      const probe = {
        x: seed.x + Math.cos(angle) * radiusPx,
        y: seed.y + Math.sin(angle) * radiusPx,
      };
      if (!pointInsideWalkableArea(map, probe)) {
        continue;
      }
      if (canOccupyPoint(map, probe, doors, radius, actor)) {
        return probe;
      }
    }
  }

  return null;
}

export function buildActorRouteToPoint(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: NavigationActor,
  targetPoint: MysteryRoomVec2,
  radius = 0,
): MysteryRoomPointNavigationRoute {
  const resolvedTarget = resolveNavigationTargetPoint(map, doors, actor, targetPoint, radius);
  const targetZoneId = resolvePointZoneId(map, resolvedTarget);

  if (
    straightPathIsTraversable(map, actor.position, resolvedTarget, doors, radius, actor) &&
    segmentHazardCost(map, actor.position, resolvedTarget) <= 0
  ) {
    return {
      nodePath: [],
      reachable: true,
      targetZoneId,
      waypoints: [{ ...resolvedTarget }],
    };
  }

  const startCandidates = collectStartNodeCandidates(map, doors, actor, actor.position, actor.zoneId, radius);
  const targetCandidates = collectTargetNodeCandidates(map, doors, actor, resolvedTarget, targetZoneId, radius);

  let bestRoute: { nodePath: string[]; score: number } | null = pickBestNodeRoute(
    map,
    doors,
    actor,
    actor.position,
    resolvedTarget,
    startCandidates,
    targetCandidates,
    radius,
  );

  if (!bestRoute) {
    const fallbackTargetCandidates = collectAnyZoneTargetNodeCandidates(map, doors, actor, resolvedTarget, radius);
    bestRoute = pickBestNodeRoute(
      map,
      doors,
      actor,
      actor.position,
      resolvedTarget,
      startCandidates,
      fallbackTargetCandidates,
      radius,
    );
  }

  if (!bestRoute) {
    const gridWaypoints = buildGridRouteToPoint(map, doors, actor, resolvedTarget, radius);
    if (gridWaypoints.length > 0) {
      return {
        nodePath: [],
        reachable: true,
        targetZoneId,
        waypoints: gridWaypoints,
      };
    }

    return {
      nodePath: [],
      reachable: false,
      targetZoneId,
      waypoints: [],
    };
  }

  const navGraph = getMysteryRoomNavGraphCache(map);
  const nodePositions = bestRoute.nodePath
    .map((nodeId) => navGraph.nodeById.get(nodeId)?.position ?? null)
    .filter((position): position is MysteryRoomVec2 => !!position)
    .map((position) => ({ ...position }));
  const prunedNodePositions = [...nodePositions];

  while (prunedNodePositions.length > 0) {
    const nextCandidate =
      prunedNodePositions.length > 1 ? prunedNodePositions[1] : resolvedTarget;

    if (
      !straightPathIsTraversable(map, actor.position, nextCandidate, doors, radius, actor) ||
      segmentHazardCost(map, actor.position, nextCandidate) > 0
    ) {
      break;
    }

    prunedNodePositions.shift();
  }

  const lastNodePosition = prunedNodePositions.at(-1) ?? null;
  const waypoints =
    lastNodePosition && distanceBetween(lastNodePosition, resolvedTarget) <= 0.001
      ? prunedNodePositions
      : [...prunedNodePositions, { ...resolvedTarget }];

  return {
    nodePath: bestRoute.nodePath,
    reachable: true,
    targetZoneId,
    waypoints,
  };
}

export function resolveImmediateRouteWaypoint(
  route: MysteryRoomPointNavigationRoute,
  origin: MysteryRoomVec2,
  arrivalMargin = NAV_WAYPOINT_ARRIVAL_MARGIN,
): MysteryRoomVec2 | null {
  for (const waypoint of route.waypoints) {
    if (distanceBetween(origin, waypoint) > arrivalMargin) {
      return waypoint;
    }
  }

  return null;
}

export function updateNpcNavigation(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  npc: MysteryRoomNpcState,
  dtMs: number,
): MysteryRoomNpcState {
  const navGraph = getMysteryRoomNavGraphCache(map);
  const currentTargetNodeId = npc.navPath[npc.navIndex] ?? npc.navPath[0] ?? null;
  const currentTargetNode = currentTargetNodeId
    ? navGraph.nodeById.get(currentTargetNodeId)
    : null;

  if (npc.currentTargetNodeId && !navGraph.nodeById.has(npc.currentTargetNodeId)) {
    return clearNavState(npc);
  }

  if (!currentTargetNode) {
    return npc;
  }

  const distance = Math.hypot(
    currentTargetNode.position.x - npc.position.x,
    currentTargetNode.position.y - npc.position.y,
  );

  if (distance <= NAV_WAYPOINT_ARRIVAL_MARGIN) {
    const nextNavIndex = Math.min(npc.navIndex + 1, Math.max(0, npc.navPath.length - 1));
    return {
      ...npc,
      stuckMs: 0,
      lastWaypointDistance: distance,
      navIndex: nextNavIndex,
    };
  }

  if (npc.lastWaypointDistance === null || distance < npc.lastWaypointDistance - 1) {
    return {
      ...npc,
      stuckMs: 0,
      lastWaypointDistance: distance,
    };
  }

  const stuckMs = npc.stuckMs + dtMs;
  if (stuckMs >= 800) {
    return clearNavState(npc);
  }

  return {
    ...npc,
    stuckMs,
    lastWaypointDistance: distance,
  };
}

function findNearestNavNodeId(
  map: MysteryRoomMapDefinition,
  position: MysteryRoomVec2,
  zoneId: string,
): string | null {
  const navGraph = getMysteryRoomNavGraphCache(map);
  const sameZone = navGraph.nodesByZoneId.get(zoneId) ?? [];
  const preferred = sameZone.length > 0 ? sameZone : map.navNodes;

  return nearestNodeId(preferred, position) ?? nearestNodeId(map.navNodes, position);
}

function collectStartNodeCandidates(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  position: MysteryRoomVec2,
  zoneId: string,
  radius: number,
) {
  const sameZoneNodes = getMysteryRoomNavGraphCache(map).nodesByZoneId.get(zoneId) ?? [];
  const directNodes = sameZoneNodes.filter((node) =>
    straightPathIsTraversable(map, position, node.position, doors, radius, actor),
  );
  const preferred = directNodes.length > 0 ? directNodes : sameZoneNodes.length > 0 ? sameZoneNodes : map.navNodes;

  return sortNodesByDistance(preferred, position).slice(0, NAV_CANDIDATE_LIMIT);
}

function collectTargetNodeCandidates(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  targetPoint: MysteryRoomVec2,
  targetZoneId: string | null,
  radius: number,
) {
  const navGraph = getMysteryRoomNavGraphCache(map);
  const sameZoneNodes = targetZoneId
    ? navGraph.nodesByZoneId.get(targetZoneId) ?? []
    : [];
  const directNodes = sameZoneNodes.filter((node) =>
    straightPathIsTraversable(map, node.position, targetPoint, doors, radius, actor),
  );
  const preferred = directNodes.length > 0 ? directNodes : sameZoneNodes.length > 0 ? sameZoneNodes : map.navNodes;

  return sortNodesByDistance(preferred, targetPoint).slice(0, NAV_CANDIDATE_LIMIT);
}

function collectAnyZoneTargetNodeCandidates(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  targetPoint: MysteryRoomVec2,
  radius: number,
) {
  const directNodes = map.navNodes.filter((node) =>
    straightPathIsTraversable(map, node.position, targetPoint, doors, radius, actor),
  );
  const preferred = directNodes.length > 0 ? directNodes : map.navNodes;

  return sortNodesByDistance(preferred, targetPoint).slice(0, NAV_CANDIDATE_LIMIT);
}

function pickBestNodeRoute(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  actorPosition: MysteryRoomVec2,
  targetPoint: MysteryRoomVec2,
  startCandidates: MysteryRoomNavNodeDefinition[],
  targetCandidates: MysteryRoomNavNodeDefinition[],
  radius: number,
) {
  let bestRoute: { nodePath: string[]; score: number } | null = null;

  for (const startNode of startCandidates) {
    for (const targetNode of targetCandidates) {
      const nodePath = shortestPath(map, doors, actor, startNode.id, targetNode.id, radius);
      if (nodePath.length === 0) {
        continue;
      }

      const score =
        segmentRouteCost(map, actorPosition, startNode.position) +
        nodePathDistanceCost(map, nodePath) +
        segmentRouteCost(map, targetNode.position, targetPoint);

      if (!bestRoute || score < bestRoute.score) {
        bestRoute = { nodePath, score };
      }
    }
  }

  return bestRoute;
}

function sortNodesByDistance(
  nodes: MysteryRoomNavNodeDefinition[],
  position: MysteryRoomVec2,
) {
  return [...nodes].sort(
    (left, right) => distanceBetween(left.position, position) - distanceBetween(right.position, position),
  );
}

function edgeIsTraversable(
  map: MysteryRoomMapDefinition,
  edge: MysteryRoomNavEdgeDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  radius: number,
): boolean {
  if (edge.requiredAccess && !actor.accessLevels.includes(edge.requiredAccess)) {
    return false;
  }

  const navGraph = getMysteryRoomNavGraphCache(map);
  const fromNode = navGraph.nodeById.get(edge.fromNodeId);
  const toNode = navGraph.nodeById.get(edge.toNodeId);

  if (!fromNode || !toNode) {
    return false;
  }

  const ignoreDoorId = edge.doorId;
  if (!straightPathIsTraversable(map, fromNode.position, toNode.position, doors, radius, actor, ignoreDoorId)) {
    return false;
  }

  if (!edge.doorId) {
    return true;
  }

  const door = doors.find((entry) => entry.id === edge.doorId);

  if (!door) {
    return false;
  }

  return door.state !== 'locked' && canDoorActorUseDoor(door, actor);
}

function shortestPath(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  startNodeId: string,
  targetNodeId: string,
  radius: number,
  options?: { allowBestApproach?: boolean },
): string[] {
  if (startNodeId === targetNodeId) {
    return [startNodeId];
  }

  const navGraph = getMysteryRoomNavGraphCache(map);
  const nodeIds = navGraph.nodeIds;
  const distances = new Map<string, number>();
  const previous = new Map<string, string | null>();
  const unvisited = new Set(nodeIds);

  for (const nodeId of nodeIds) {
    distances.set(nodeId, Number.POSITIVE_INFINITY);
    previous.set(nodeId, null);
  }

  distances.set(startNodeId, 0);

  while (unvisited.size > 0) {
    let currentNodeId: string | null = null;
    let currentDistance = Number.POSITIVE_INFINITY;

    for (const nodeId of Array.from(unvisited)) {
      const distance = distances.get(nodeId) ?? Number.POSITIVE_INFINITY;
      if (distance < currentDistance) {
        currentDistance = distance;
        currentNodeId = nodeId;
      }
    }

    if (!currentNodeId || currentDistance === Number.POSITIVE_INFINITY) {
      break;
    }

    unvisited.delete(currentNodeId);

    if (currentNodeId === targetNodeId) {
      break;
    }

    for (const edge of navGraph.outgoingEdgesByNodeId.get(currentNodeId) ?? []) {
      if (!edgeIsTraversable(map, edge, doors, actor, radius)) {
        continue;
      }

      const fromNode = navGraph.nodeById.get(edge.fromNodeId);
      const toNode = navGraph.nodeById.get(edge.toNodeId);
      const routeCost = fromNode && toNode ? segmentRouteCost(map, fromNode.position, toNode.position) : edge.distanceCost;
      const nextDistance = currentDistance + edge.distanceCost + routeCost;
      if (nextDistance < (distances.get(edge.toNodeId) ?? Number.POSITIVE_INFINITY)) {
        distances.set(edge.toNodeId, nextDistance);
        previous.set(edge.toNodeId, currentNodeId);
      }
    }
  }

  if ((distances.get(targetNodeId) ?? Number.POSITIVE_INFINITY) === Number.POSITIVE_INFINITY) {
    if (options?.allowBestApproach === false) {
      return [];
    }

    return reconstructBestApproachNodePath(map, distances, previous, startNodeId, targetNodeId);
  }

  const path: string[] = [];
  let cursor: string | null = targetNodeId;

  while (cursor) {
    path.push(cursor);
    cursor = previous.get(cursor) ?? null;
  }

  path.reverse();

  return path[0] === startNodeId ? path : [];
}

/** Если целевой узел недостижим (например, за запертой дверью), идём к ближайшему узлу по геометрии к цели. */
function reconstructBestApproachNodePath(
  map: MysteryRoomMapDefinition,
  distances: Map<string, number>,
  previous: Map<string, string | null>,
  startNodeId: string,
  targetNodeId: string,
): string[] {
  const goal = getMysteryRoomNavGraphCache(map).nodeById.get(targetNodeId)?.position ?? null;
  if (!goal) {
    return [];
  }

  let bestId: string | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const node of map.navNodes) {
    const d = distances.get(node.id) ?? Number.POSITIVE_INFINITY;
    if (!Number.isFinite(d) || d === Number.POSITIVE_INFINITY) {
      continue;
    }
    const score = distanceBetween(node.position, goal) + d * 1e-6;
    if (score < bestScore) {
      bestScore = score;
      bestId = node.id;
    }
  }

  if (!bestId || bestId === startNodeId) {
    return [];
  }

  const path: string[] = [];
  let cursor: string | null = bestId;
  while (cursor) {
    path.push(cursor);
    cursor = previous.get(cursor) ?? null;
  }
  path.reverse();
  return path[0] === startNodeId ? path : [];
}

function nodePathDistanceCost(
  map: MysteryRoomMapDefinition,
  nodePath: string[],
) {
  let total = 0;
  const edgeByNodePair = getMysteryRoomNavGraphCache(map).edgeByNodePair;

  for (let index = 0; index < nodePath.length - 1; index += 1) {
    const edge = edgeByNodePair.get(navNodePairKey(nodePath[index], nodePath[index + 1]));
    total += edge?.distanceCost ?? 0;
  }

  return total;
}

function nearestNodeId(
  nodes: MysteryRoomNavNodeDefinition[],
  position: MysteryRoomVec2,
): string | null {
  let bestNodeId: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const node of nodes) {
    const distance = Math.hypot(node.position.x - position.x, node.position.y - position.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestNodeId = node.id;
    }
  }

  return bestNodeId;
}

function straightPathIsTraversable(
  map: MysteryRoomMapDefinition,
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
  radius: number,
  actor: DoorNavigationActor,
  ignoreDoorId?: string,
) {
  if (!canOccupyPoint(map, to, doors, radius, actor, ignoreDoorId)) {
    return false;
  }

  if (movementCrossesWall(map.walls, from, to, radius)) {
    return false;
  }

  const distance = distanceBetween(from, to);
  const stepDistance = Math.max(12, radius > 0 ? radius * 0.75 : 12);
  const steps = Math.max(1, Math.ceil(distance / stepDistance));

  for (let index = 1; index <= steps; index += 1) {
    const t = index / steps;
    const sample = {
      x: from.x + (to.x - from.x) * t,
      y: from.y + (to.y - from.y) * t,
    };

    if (!canOccupyPoint(map, sample, doors, radius, actor, ignoreDoorId)) {
      return false;
    }
  }

  return true;
}

function canOccupyPoint(
  map: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
  radius: number,
  actor: DoorNavigationActor,
  ignoreDoorId?: string,
) {
  const samples = [
    point,
    { x: point.x - radius, y: point.y },
    { x: point.x + radius, y: point.y },
    { x: point.x, y: point.y - radius },
    { x: point.x, y: point.y + radius },
  ];

  return samples.every(
    (sample) => pointInsideWalkableArea(map, sample) && !pointHitsMovementBlocker(map, sample, doors, actor, ignoreDoorId),
  );
}

function pointInsideWalkableArea(map: MysteryRoomMapDefinition, point: MysteryRoomVec2) {
  return map.rooms.some((room) => room.floor.some((floor) => rectContainsPoint(floor, point)));
}

function pointHitsMovementBlocker(
  map: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  ignoreDoorId?: string,
) {
  if (map.occluders.some((occluder) => occluder.blocksMovement && rectContainsPoint(occluder.rect, point))) {
    return true;
  }

  if (
    doors.some(
      (door) =>
        door.id !== ignoreDoorId && !isDoorPlannable(door, actor) && rectContainsPoint(door.panel, point),
    )
  ) {
    return true;
  }

  return (map.customObjects ?? []).some((object) => customObjectBlocksPoint(object, point));
}

function isDoorPlannable(door: MysteryRoomDoorRuntimeState, actor: DoorNavigationActor) {
  if (isDoorTraversable(door)) {
    return true;
  }

  return door.state !== 'locked' && canDoorActorUseDoor(door, actor);
}

function isDoorTraversable(door: MysteryRoomDoorRuntimeState) {
  if (door.state === 'open') {
    return true;
  }

  return door.openProgress >= 0.72 && (door.state === 'opening' || door.state === 'closing');
}

function movementCrossesWall(
  walls: MysteryRoomMapDefinition['walls'],
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
  radius: number,
) {
  if (from.x === to.x && from.y === to.y) {
    return false;
  }

  for (const offset of sampleOffsets(radius)) {
    const sampleStart = {
      x: from.x + offset.x,
      y: from.y + offset.y,
    };
    const sampleEnd = {
      x: to.x + offset.x,
      y: to.y + offset.y,
    };

    if (walls.some((wall) => segmentsIntersect(sampleStart, sampleEnd, wall.start, wall.end))) {
      return true;
    }
  }

  return false;
}

function sampleOffsets(radius: number) {
  return [
    { x: 0, y: 0 },
    { x: -radius, y: 0 },
    { x: radius, y: 0 },
    { x: 0, y: -radius },
    { x: 0, y: radius },
  ];
}

function segmentsIntersect(
  aStart: MysteryRoomVec2,
  aEnd: MysteryRoomVec2,
  bStart: MysteryRoomVec2,
  bEnd: MysteryRoomVec2,
) {
  const d1 = direction(bStart, bEnd, aStart);
  const d2 = direction(bStart, bEnd, aEnd);
  const d3 = direction(aStart, aEnd, bStart);
  const d4 = direction(aStart, aEnd, bEnd);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  return (
    (Math.abs(d1) <= 0.0001 && pointOnSegment(bStart, bEnd, aStart)) ||
    (Math.abs(d2) <= 0.0001 && pointOnSegment(bStart, bEnd, aEnd)) ||
    (Math.abs(d3) <= 0.0001 && pointOnSegment(aStart, aEnd, bStart)) ||
    (Math.abs(d4) <= 0.0001 && pointOnSegment(aStart, aEnd, bEnd))
  );
}

function direction(a: MysteryRoomVec2, b: MysteryRoomVec2, c: MysteryRoomVec2) {
  return (c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x);
}

function pointOnSegment(a: MysteryRoomVec2, b: MysteryRoomVec2, p: MysteryRoomVec2) {
  return (
    p.x >= Math.min(a.x, b.x) - 0.0001 &&
    p.x <= Math.max(a.x, b.x) + 0.0001 &&
    p.y >= Math.min(a.y, b.y) - 0.0001 &&
    p.y <= Math.max(a.y, b.y) + 0.0001
  );
}

function resolvePointZoneId(
  map: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
) {
  return map.rooms.find((room) => room.floor.some((floor) => rectContainsPoint(floor, point)))?.id ?? null;
}

function distanceBetween(left: MysteryRoomVec2, right: MysteryRoomVec2) {
  return Math.hypot(left.x - right.x, left.y - right.y);
}

function buildGridRouteToPoint(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor & { position: MysteryRoomVec2 },
  targetPoint: MysteryRoomVec2,
  radius: number,
): MysteryRoomVec2[] {
  if (!canOccupyPoint(map, actor.position, doors, radius, actor) || !canOccupyPoint(map, targetPoint, doors, radius, actor)) {
    return [];
  }

  const bounds = routeSearchBounds(map, actor.position, targetPoint);
  const cellSize = Math.max(GRID_ROUTE_CELL_SIZE, Math.ceil(radius * 2.5));
  const startKey = 'start';
  const targetKey = 'target';
  const nodes = new Map<string, { key: string; position: MysteryRoomVec2; g: number; f: number; previous: string | null }>();
  const open = new Set<string>([startKey]);
  const closed = new Set<string>();

  nodes.set(startKey, {
    key: startKey,
    position: { ...actor.position },
    g: 0,
    f: distanceBetween(actor.position, targetPoint),
    previous: null,
  });

  while (open.size > 0 && closed.size < GRID_ROUTE_VISIT_LIMIT) {
    const currentKey = pickLowestScoreNode(open, nodes);
    if (!currentKey) {
      break;
    }

    if (currentKey === targetKey) {
      return reconstructGridWaypoints(nodes, targetKey);
    }

    open.delete(currentKey);
    closed.add(currentKey);
    const current = nodes.get(currentKey);
    if (!current) {
      continue;
    }

    for (const neighbor of gridRouteNeighbors(map, doors, actor, current.position, targetPoint, bounds, cellSize, radius)) {
      if (closed.has(neighbor.key)) {
        continue;
      }

      const nextG = current.g + segmentRouteCost(map, current.position, neighbor.position);
      const known = nodes.get(neighbor.key);
      if (known && nextG >= known.g) {
        continue;
      }

      nodes.set(neighbor.key, {
        key: neighbor.key,
        position: neighbor.position,
        g: nextG,
        f: nextG + distanceBetween(neighbor.position, targetPoint),
        previous: currentKey,
      });
      open.add(neighbor.key);
    }
  }

  return [];
}

function routeSearchBounds(
  map: MysteryRoomMapDefinition,
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
) {
  const padding = 240;
  const floorRects = map.rooms.flatMap((room) => room.floor);
  if (floorRects.length === 0) {
    return { minX: 0, minY: 0, maxX: map.width, maxY: map.height };
  }
  const minX = Math.max(0, Math.min(from.x, to.x, ...floorRects.map((rect) => rect.x)) - padding);
  const minY = Math.max(0, Math.min(from.y, to.y, ...floorRects.map((rect) => rect.y)) - padding);
  const maxX = Math.min(map.width, Math.max(from.x, to.x, ...floorRects.map((rect) => rect.x + rect.width)) + padding);
  const maxY = Math.min(map.height, Math.max(from.y, to.y, ...floorRects.map((rect) => rect.y + rect.height)) + padding);

  return { minX, minY, maxX, maxY };
}

function pickLowestScoreNode(
  open: Set<string>,
  nodes: Map<string, { f: number }>,
) {
  let bestKey: string | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const key of Array.from(open)) {
    const score = nodes.get(key)?.f ?? Number.POSITIVE_INFINITY;
    if (score < bestScore) {
      bestScore = score;
      bestKey = key;
    }
  }

  return bestKey;
}

function gridRouteNeighbors(
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  actor: DoorNavigationActor,
  position: MysteryRoomVec2,
  targetPoint: MysteryRoomVec2,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  cellSize: number,
  radius: number,
) {
  const neighbors: { key: string; position: MysteryRoomVec2 }[] = [];

  if (straightPathIsTraversable(map, position, targetPoint, doors, radius, actor)) {
    neighbors.push({ key: 'target', position: { ...targetPoint } });
  }

  const baseCell = pointToGridCell(position, bounds, cellSize);
  const directions = [
    { x: -1, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: -1 },
    { x: 0, y: 1 },
    { x: -1, y: -1 },
    { x: 1, y: -1 },
    { x: -1, y: 1 },
    { x: 1, y: 1 },
  ];

  for (const direction of directions) {
    const cell = { x: baseCell.x + direction.x, y: baseCell.y + direction.y };
    const candidate = gridCellCenter(cell, bounds, cellSize);
    if (candidate.x < bounds.minX || candidate.x > bounds.maxX || candidate.y < bounds.minY || candidate.y > bounds.maxY) {
      continue;
    }

    if (!canOccupyPoint(map, candidate, doors, radius, actor)) {
      continue;
    }

    if (
      Math.abs(direction.x) === 1 &&
      Math.abs(direction.y) === 1 &&
      (!canOccupyPoint(map, gridCellCenter({ x: baseCell.x + direction.x, y: baseCell.y }, bounds, cellSize), doors, radius, actor) ||
        !canOccupyPoint(map, gridCellCenter({ x: baseCell.x, y: baseCell.y + direction.y }, bounds, cellSize), doors, radius, actor))
    ) {
      continue;
    }

    if (!straightPathIsTraversable(map, position, candidate, doors, radius, actor)) {
      continue;
    }

    neighbors.push({
      key: `${cell.x}:${cell.y}`,
      position: candidate,
    });
  }

  return neighbors;
}

function pointToGridCell(
  point: MysteryRoomVec2,
  bounds: { minX: number; minY: number },
  cellSize: number,
) {
  return {
    x: Math.floor((point.x - bounds.minX) / cellSize),
    y: Math.floor((point.y - bounds.minY) / cellSize),
  };
}

function gridCellCenter(
  cell: MysteryRoomVec2,
  bounds: { minX: number; minY: number },
  cellSize: number,
) {
  return {
    x: bounds.minX + cell.x * cellSize + cellSize / 2,
    y: bounds.minY + cell.y * cellSize + cellSize / 2,
  };
}

function reconstructGridWaypoints(
  nodes: Map<string, { position: MysteryRoomVec2; previous: string | null }>,
  targetKey: string,
) {
  const waypoints: MysteryRoomVec2[] = [];
  let cursor: string | null = targetKey;

  while (cursor) {
    const node = nodes.get(cursor);
    if (!node) {
      break;
    }
    waypoints.push({ ...node.position });
    cursor = node.previous;
  }

  waypoints.reverse();
  if (waypoints.length > 0) {
    waypoints.shift();
  }

  return pruneGridWaypoints(waypoints);
}

function pruneGridWaypoints(waypoints: MysteryRoomVec2[]) {
  if (waypoints.length <= 2) {
    return waypoints;
  }

  const pruned: MysteryRoomVec2[] = [];
  for (const waypoint of waypoints) {
    const previous = pruned.at(-1);
    if (!previous || distanceBetween(previous, waypoint) > 1) {
      pruned.push(waypoint);
    }
  }

  return pruned;
}

function segmentRouteCost(
  map: MysteryRoomMapDefinition,
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
) {
  const distance = distanceBetween(from, to);
  return distance + segmentHazardCost(map, from, to);
}

function segmentHazardCost(
  map: MysteryRoomMapDefinition,
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
) {
  if (map.hazards.length === 0) {
    return 0;
  }

  const distance = distanceBetween(from, to);
  const steps = Math.max(1, Math.ceil(distance / 24));
  let totalRisk = 0;

  for (let index = 0; index <= steps; index += 1) {
    const t = index / steps;
    const sample = {
      x: from.x + (to.x - from.x) * t,
      y: from.y + (to.y - from.y) * t,
    };
    totalRisk += pointHazardRisk(map, sample);
  }

  return distance * (totalRisk / (steps + 1)) * HAZARD_ROUTE_COST_MULTIPLIER;
}

function pointHazardRisk(map: MysteryRoomMapDefinition, point: MysteryRoomVec2) {
  return map.hazards.reduce((total, hazard) => {
    const distance = distanceBetween(point, hazard.position);
    if (distance > hazard.radius) {
      return total;
    }
    const falloff = 1 - distance / Math.max(1, hazard.radius);
    return total + Math.max(0.1, hazard.intensity) * falloff;
  }, 0);
}

function customObjectBlocksPoint(object: MysteryRoomCustomObjectDefinition, point: MysteryRoomVec2) {
  if (!customObjectBlocksMovement(object)) {
    return false;
  }

  if (!pointHitsCollisionShape(object.collision, point)) {
    return false;
  }

  return !object.assetAlphaCollisionEnabled || isMysteryRoomCustomObjectAlphaOpaqueAtPoint(object, point) !== false;
}

function customObjectBlocksMovement(object: MysteryRoomCustomObjectDefinition) {
  if (object.collision.kind !== 'rect' && object.collision.kind !== 'circle' && object.collision.kind !== 'polygon') {
    return false;
  }

  return (object.collisionEnabled ?? object.collision.blocksMovement) && object.collision.blocksMovement;
}

function pointHitsCollisionShape(collision: MysteryRoomCollisionShape, point: MysteryRoomVec2) {
  if (collision.kind === 'rect') {
    return rectContainsPoint(collision.rect, point);
  }
  if (collision.kind === 'circle') {
    return distanceBetween(collision.center, point) <= collision.radius;
  }
  if (collision.kind === 'polygon') {
    return pointInPolygon(point, collision.points);
  }
  return false;
}

function pointInPolygon(point: MysteryRoomVec2, polygon: MysteryRoomVec2[]) {
  if (polygon.length < 3) {
    return false;
  }

  let inside = false;
  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
    const current = polygon[index];
    const previous = polygon[previousIndex];
    const denominator = previous.y - current.y;
    const crosses =
      current.y > point.y !== previous.y > point.y &&
      Math.abs(denominator) > 0.000001 &&
      point.x < ((previous.x - current.x) * (point.y - current.y)) / denominator + current.x;
    if (crosses) {
      inside = !inside;
    }
  }

  return inside;
}

function clearNavState(npc: MysteryRoomNpcState): MysteryRoomNpcState {
  return {
    ...npc,
    navPath: [],
    navIndex: 0,
    currentTargetNodeId: null,
    stuckMs: 0,
    lastWaypointDistance: null,
  };
}
